import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { CheckSquare, Square, Package } from 'lucide-react';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import { useApp } from '../../context/AppContext';
import './PrinterAssignmentModal.css';

// Componente de fila memoizado para mejor rendimiento
const ProductRow = memo(({
    producto,
    isSelected,
    currentPrinter,
    hasChange,
    toggleProduct,
    handleIndividualPrinterChange,
    impresoras
}) => {
    return (
        <div className={`virtual-row ${isSelected ? 'selected' : ''} ${hasChange ? 'has-change' : ''}`}>
            <div className="virtual-cell checkbox-col">
                <button
                    className="select-btn"
                    onClick={() => toggleProduct(producto.id)}
                >
                    {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                </button>
            </div>
            <div className="virtual-cell cell-producto">
                <div className="product-name">
                    <Package size={14} />
                    <span title={producto.nombre}>{producto.nombre}</span>
                </div>
            </div>
            <div className="virtual-cell cell-codigo">
                <span className="product-code">{producto.codigo_corto || '-'}</span>
            </div>
            <div className="virtual-cell cell-barras">
                <span className="product-code">{producto.codigo_barras || '-'}</span>
            </div>
            <div className="virtual-cell cell-categoria">
                <span className="category-badge">{producto.categoria_nombre || 'Sin categoría'}</span>
            </div>
            <div className="virtual-cell cell-almacen">
                <span className="almacen-badge">{producto.almacen_nombre || 'Sin almacén'}</span>
            </div>
            <div className="virtual-cell cell-impresora">
                <select
                    value={currentPrinter}
                    onChange={(e) => handleIndividualPrinterChange(producto.id, e.target.value)}
                    className={`printer-select-inline ${hasChange ? 'changed' : ''}`}
                >
                    <option value="">Sin impresora</option>
                    <option value="__local__">Local</option>
                    {impresoras.map(printer => (
                        <option key={printer.name} value={printer.name}>
                            {printer.name}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
});

const ITEMS_PER_LOAD = 50;

const PrinterAssignmentModal = ({ productos, onClose, onSave }) => {
    const { showToast } = useApp();
    const [impresoras, setImpresoras] = useState([]);
    const [loadingImpresoras, setLoadingImpresoras] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedPrinter, setSelectedPrinter] = useState('');
    const [saving, setSaving] = useState(false);
    const [productPrinters, setProductPrinters] = useState({});
    const [visibleCount, setVisibleCount] = useState(ITEMS_PER_LOAD);
    const scrollContainerRef = useRef(null);

    // Filtros por columna
    const [filters, setFilters] = useState({
        nombre: '',
        codigo_corto: '',
        codigo_barras: '',
        categoria: '',
        almacen: '',
        impresora: ''
    });

    // Impresoras de Windows a ocultar
    const hiddenPrinters = [
        'microsoft print to pdf',
        'microsoft xps document writer',
        'onenote',
        'onenote (desktop)',
        'onenote for windows 10',
        'fax',
        'send to onenote',
        'snagit',
        'adobe pdf',
        'foxit pdf',
        'nitro pdf'
    ];

    // Cargar impresoras del sistema
    const loadImpresoras = useCallback(async () => {
        if (!window.electron?.ipcRenderer) {
            console.warn('No se encontró Electron IPC');
            return;
        }

        try {
            setLoadingImpresoras(true);
            const printers = await window.electron.ipcRenderer.invoke('get-all-printers');
            if (Array.isArray(printers)) {
                const filteredPrinters = printers.filter(printer => {
                    const name = printer.name?.toLowerCase() || '';
                    return !hiddenPrinters.some(hidden => name.includes(hidden));
                });
                setImpresoras(filteredPrinters);
            }
        } catch (error) {
            console.error('Error al cargar impresoras:', error);
        } finally {
            setLoadingImpresoras(false);
        }
    }, []);

    useEffect(() => {
        loadImpresoras();

        const initialPrinters = {};
        productos.forEach(p => {
            initialPrinters[p.id] = p.impresora || '';
        });
        setProductPrinters(initialPrinters);
    }, [loadImpresoras, productos]);

    const handleFilterChange = (column, value) => {
        setFilters(prev => ({
            ...prev,
            [column]: value
        }));
        setVisibleCount(ITEMS_PER_LOAD); // Reset al filtrar
    };

    const clearFilters = () => {
        setFilters({
            nombre: '',
            codigo_corto: '',
            codigo_barras: '',
            categoria: '',
            almacen: '',
            impresora: ''
        });
        setVisibleCount(ITEMS_PER_LOAD);
    };

    const hasActiveFilters = Object.values(filters).some(f => f && f.trim() !== '');

    // Memoizar productos filtrados
    const filteredProducts = useMemo(() => {
        return productos.filter(producto => {
            const currentPrinter = productPrinters[producto.id] || '';

            if (filters.nombre && !producto.nombre?.toLowerCase().includes(filters.nombre.toLowerCase())) {
                return false;
            }
            if (filters.codigo_corto && !producto.codigo_corto?.toLowerCase().includes(filters.codigo_corto.toLowerCase())) {
                return false;
            }
            if (filters.codigo_barras && !producto.codigo_barras?.toLowerCase().includes(filters.codigo_barras.toLowerCase())) {
                return false;
            }
            if (filters.categoria && !producto.categoria_nombre?.toLowerCase().includes(filters.categoria.toLowerCase())) {
                return false;
            }
            if (filters.almacen && !producto.almacen_nombre?.toLowerCase().includes(filters.almacen.toLowerCase())) {
                return false;
            }
            if (filters.impresora) {
                if (filters.impresora === '__sin_impresora__') {
                    if (currentPrinter !== '') return false;
                } else if (filters.impresora === '__local__') {
                    if (currentPrinter !== '__local__') return false;
                } else if (!currentPrinter.toLowerCase().includes(filters.impresora.toLowerCase())) {
                    return false;
                }
            }
            return true;
        });
    }, [productos, productPrinters, filters]);

    // Productos visibles (lazy loading)
    const visibleProducts = useMemo(() => {
        return filteredProducts.slice(0, visibleCount);
    }, [filteredProducts, visibleCount]);

    const hasMore = visibleCount < filteredProducts.length;

    // Manejar scroll para cargar más
    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        // Cargar más cuando estemos cerca del final (100px antes)
        if (scrollHeight - scrollTop - clientHeight < 100 && hasMore) {
            setVisibleCount(prev => Math.min(prev + ITEMS_PER_LOAD, filteredProducts.length));
        }
    }, [hasMore, filteredProducts.length]);

    const toggleSelectAll = useCallback(() => {
        const allFilteredIds = filteredProducts.map(p => p.id);
        const allSelected = allFilteredIds.every(id => selectedProducts.includes(id));

        if (allSelected) {
            setSelectedProducts(prev => prev.filter(id => !allFilteredIds.includes(id)));
        } else {
            setSelectedProducts(prev => [...new Set([...prev, ...allFilteredIds])]);
        }
    }, [filteredProducts, selectedProducts]);

    const toggleProduct = useCallback((productId) => {
        setSelectedProducts(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            } else {
                return [...prev, productId];
            }
        });
    }, []);

    const handleIndividualPrinterChange = useCallback((productId, printerName) => {
        setProductPrinters(prev => ({
            ...prev,
            [productId]: printerName
        }));
    }, []);

    const assignPrinterToSelected = useCallback(() => {
        setProductPrinters(prev => {
            const updated = { ...prev };
            selectedProducts.forEach(productId => {
                updated[productId] = selectedPrinter;
            });
            return updated;
        });
    }, [selectedPrinter, selectedProducts]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const productsToUpdate = productos
                .filter(p => productPrinters[p.id] !== (p.impresora || ''))
                .map(p => ({
                    id: p.id,
                    impresora: productPrinters[p.id] || null
                }));

            if (productsToUpdate.length === 0) {
                showToast('No hay cambios para guardar', 'warning');
                setSaving(false);
                return;
            }

            await onSave(productsToUpdate);
            onClose();
        } catch (error) {
            console.error('Error al guardar:', error);
            showToast('Error al guardar los cambios', 'error');
        } finally {
            setSaving(false);
        }
    };

    const allFilteredIds = filteredProducts.map(p => p.id);
    const allSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedProducts.includes(id));
    const pendingChanges = productos.filter(p => productPrinters[p.id] !== (p.impresora || '')).length;

    return (
        <Modal
            title="Asignar Impresoras a Productos"
            onClose={onClose}
            size="xlarge"
        >
            <div className="printer-assignment-modal">
                {/* Controles superiores */}
                <div className="assignment-controls">
                    <div className="controls-info">
                        <span className="products-count">
                            {filteredProducts.length} de {productos.length} productos
                            {hasMore && ` (mostrando ${visibleCount})`}
                        </span>
                        {hasActiveFilters && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={clearFilters}
                            >
                                Limpiar filtros
                            </Button>
                        )}
                    </div>

                    <div className="bulk-assignment">
                        <select
                            value={selectedPrinter}
                            onChange={(e) => setSelectedPrinter(e.target.value)}
                            className="printer-select"
                            disabled={loadingImpresoras}
                        >
                            <option value="">Seleccionar impresora...</option>
                            <option value="">Sin impresora</option>
                            <option value="__local__">Local</option>
                            {impresoras.map(printer => (
                                <option key={printer.name} value={printer.name}>
                                    {printer.name} {printer.isDefault ? '(Pred.)' : ''}
                                </option>
                            ))}
                        </select>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={assignPrinterToSelected}
                            disabled={selectedProducts.length === 0}
                        >
                            Asignar a seleccionados ({selectedProducts.length})
                        </Button>
                    </div>
                </div>

                {/* Tabla */}
                <div className="virtual-table-container">
                    {/* Header fijo */}
                    <div className="virtual-header">
                        <div className="virtual-header-row">
                            <div className="virtual-cell checkbox-col">
                                <button
                                    className="select-all-btn"
                                    onClick={toggleSelectAll}
                                    title={allSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
                                >
                                    {allSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                                </button>
                            </div>
                            <div className="virtual-cell cell-producto">Producto</div>
                            <div className="virtual-cell cell-codigo">Cód. Corto</div>
                            <div className="virtual-cell cell-barras">Cód. Barras</div>
                            <div className="virtual-cell cell-categoria">Categoría</div>
                            <div className="virtual-cell cell-almacen">Almacén</div>
                            <div className="virtual-cell cell-impresora">Impresora</div>
                        </div>
                        <div className="virtual-filter-row">
                            <div className="virtual-cell checkbox-col"></div>
                            <div className="virtual-cell cell-producto">
                                <input
                                    type="text"
                                    className="column-filter"
                                    placeholder="Filtrar..."
                                    value={filters.nombre}
                                    onChange={(e) => handleFilterChange('nombre', e.target.value)}
                                />
                            </div>
                            <div className="virtual-cell cell-codigo">
                                <input
                                    type="text"
                                    className="column-filter"
                                    placeholder="Filtrar..."
                                    value={filters.codigo_corto}
                                    onChange={(e) => handleFilterChange('codigo_corto', e.target.value)}
                                />
                            </div>
                            <div className="virtual-cell cell-barras">
                                <input
                                    type="text"
                                    className="column-filter"
                                    placeholder="Filtrar..."
                                    value={filters.codigo_barras}
                                    onChange={(e) => handleFilterChange('codigo_barras', e.target.value)}
                                />
                            </div>
                            <div className="virtual-cell cell-categoria">
                                <input
                                    type="text"
                                    className="column-filter"
                                    placeholder="Filtrar..."
                                    value={filters.categoria}
                                    onChange={(e) => handleFilterChange('categoria', e.target.value)}
                                />
                            </div>
                            <div className="virtual-cell cell-almacen">
                                <input
                                    type="text"
                                    className="column-filter"
                                    placeholder="Filtrar..."
                                    value={filters.almacen}
                                    onChange={(e) => handleFilterChange('almacen', e.target.value)}
                                />
                            </div>
                            <div className="virtual-cell cell-impresora">
                                <select
                                    className="column-filter-select"
                                    value={filters.impresora}
                                    onChange={(e) => handleFilterChange('impresora', e.target.value)}
                                >
                                    <option value="">Todas</option>
                                    <option value="__sin_impresora__">Sin impresora</option>
                                    <option value="__local__">Local</option>
                                    {impresoras.map(printer => (
                                        <option key={printer.name} value={printer.name}>
                                            {printer.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Lista de productos con scroll infinito */}
                    <div
                        className="virtual-body"
                        ref={scrollContainerRef}
                        onScroll={handleScroll}
                    >
                        {visibleProducts.length > 0 ? (
                            <>
                                {visibleProducts.map(producto => {
                                    const isSelected = selectedProducts.includes(producto.id);
                                    const currentPrinter = productPrinters[producto.id] || '';
                                    const originalPrinter = producto.impresora || '';
                                    const hasChange = currentPrinter !== originalPrinter;

                                    return (
                                        <ProductRow
                                            key={producto.id}
                                            producto={producto}
                                            isSelected={isSelected}
                                            currentPrinter={currentPrinter}
                                            hasChange={hasChange}
                                            toggleProduct={toggleProduct}
                                            handleIndividualPrinterChange={handleIndividualPrinterChange}
                                            impresoras={impresoras}
                                        />
                                    );
                                })}
                                {hasMore && (
                                    <div className="loading-more">
                                        Desplaza para cargar más...
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="empty-state">
                                <Package size={48} />
                                <p>No se encontraron productos</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer con acciones */}
                <div className="assignment-footer">
                    <div className="footer-info">
                        {pendingChanges > 0 && (
                            <span className="pending-changes">
                                {pendingChanges} cambio{pendingChanges !== 1 ? 's' : ''} pendiente{pendingChanges !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    <div className="footer-actions">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            disabled={saving}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving || pendingChanges === 0}
                        >
                            {saving ? 'Guardando...' : `Guardar Cambios (${pendingChanges})`}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default PrinterAssignmentModal;
